import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { AuditIssue, AuditSeverity } from '@/utils/api-pb';

interface IssueCardProps {
  issue: AuditIssue;
  /** Скрывает разбор за пейволлом: видны только заголовок и серьёзность. */
  locked?: boolean;
  onFixInBuilder?: (issue: AuditIssue) => void;
  onUpgrade?: () => void;
}

const SEVERITY_STYLES: Record<AuditSeverity, string> = {
  critical: 'bg-[var(--error-soft,#ffeded)] text-[color:var(--error-strong,#b3261e)]',
  high: 'bg-[#fff4e5] text-[#a35b00]',
  medium: 'bg-[#fff9e5] text-[#8a6d00]',
  low: 'bg-[var(--grey-background,#f8f8f8)] text-[color:var(--grey-text,#565858)]',
};

/**
 * One finding, collapsed by default.
 *
 * A full report runs to forty of these, and a page that opens all of them at once is a
 * wall nobody reads. Collapsed, the list is scannable by severity; expanded, one
 * finding gives the evidence, what to do, and a prompt to paste into an AI tool.
 *
 * `evidence.value` is rendered as text inside a `<pre>`, never as markup: it is a
 * fragment of somebody else's page, and it arrives in the report verbatim.
 */
export function IssueCard({ issue, locked, onFixInBuilder, onUpgrade }: IssueCardProps) {
  const { t } = useTranslation('siteAudit');
  const [open, setOpen] = useState(false);

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(issue.fixPrompt);
      toast.success(t('report.promptCopied'));
    } catch {
      toast.error(t('report.promptCopyFailed'));
    }
  }

  return (
    <li className="rounded-[16px] border border-[var(--grey-accent,#eeeeee)]">
      <button
        type="button"
        onClick={() => (locked ? onUpgrade?.() : setOpen((current) => !current))}
        aria-expanded={locked ? undefined : open}
        className="flex w-full items-start gap-[12px] px-[16px] py-[14px] text-left"
      >
        <span
          className={cn(
            'mt-[2px] shrink-0 rounded-[6px] px-[8px] py-[2px] font-segoe text-[12px] font-semibold',
            SEVERITY_STYLES[issue.severity],
          )}
        >
          {t(`severity.${issue.severity}`)}
        </span>

        <span className="flex flex-1 flex-col gap-[2px]">
          <span className="font-manrope text-[15px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
            {issue.title}
          </span>
          {locked && (
            <span className="font-segoe text-[13px] text-[color:var(--main-brand,#2197f1)]">
              {t('report.lockedHint')}
            </span>
          )}
        </span>

        {!locked && (
          <span aria-hidden="true" className="mt-[2px] font-segoe text-[13px] text-[color:var(--grey-text,#969696)]">
            {open ? '−' : '+'}
          </span>
        )}
      </button>

      {open && !locked && (
        <div className="flex flex-col gap-[12px] border-t border-[var(--grey-accent,#eeeeee)] px-[16px] py-[14px]">
          <p className="font-segoe text-[14px] leading-[20px] text-[color:var(--black-text,#0d0d0d)]">
            {issue.description}
          </p>

          {issue.evidence?.value && (
            <div className="flex flex-col gap-[4px]">
              <span className="font-segoe text-[12px] text-[color:var(--grey-text,#969696)]">
                {t('report.evidence')}
                {issue.evidence.location ? ` · ${issue.evidence.location}` : ''}
              </span>
              <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-[12px] bg-[var(--grey-background,#f8f8f8)] px-[12px] py-[8px] font-mono text-[12px] text-[color:var(--black-text,#0d0d0d)]">
                {issue.evidence.value}
              </pre>
            </div>
          )}

          {issue.recommendation && (
            <p className="font-segoe text-[14px] leading-[20px] text-[color:var(--black-text,#0d0d0d)]">
              {issue.recommendation}
            </p>
          )}

          <div className="flex flex-wrap gap-[8px]">
            {issue.fixPrompt && (
              <Button variant="brand-outline" size="brand-sm" onClick={copyPrompt}>
                {t('report.copyPrompt')}
              </Button>
            )}
            {/* Только для сайтов из билдера: правка уходит прямо в чат редактора. */}
            {issue.builderAction && onFixInBuilder && (
              <Button variant="brand" size="brand-sm" onClick={() => onFixInBuilder(issue)}>
                {t('report.fixInBuilder')}
              </Button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
